import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { AuxiliarService } from '../../services/auxiliar.service';
import { ChangePasswordDrawerComponent } from '../drawers/change-password-drawer/change-password-drawer.component';
import _ from 'lodash';
import moment from 'moment';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Tenant } from '../../models/tenant';
import { distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import { TenantService } from '../../services/tenant.service';
import { NavigationEnd, Router, Event } from '@angular/router';
import { defaultIfEmpty, filter, finalize, takeUntil } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { TimesheetService } from '../../services/timesheet.service';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit {
  @ViewChild('menubarButton', { static: false }) menubarButton!: ElementRef;

  private activeItem: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public isDev: boolean;
  public items: MenuItem[] | undefined;
  public itemsUser: MenuItem[] | undefined;
  private lastTimesheetRecord: any;
  public loadingTimesheet: boolean;
  public logo: string | undefined;
  public menuBreakpoint: string = '1400px';
  private roles: string[] = [];
  public showMenu = true;
  public showMenuFade = true;
  public tenants: Tenant[] = [];
  public username: string;

  get f(): { [index: string]: any; } {
    return this.form.controls;
  }

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private auxiliarService: AuxiliarService,
    private formBuilder: FormBuilder,
    private tenantService: TenantService,
    private router: Router,
    private cookieService: CookieService,
    private timesheetService: TimesheetService,
    private logger: NGXLogger,
  ){}

  async ngOnInit() {
    // is dev
    this.isDev = _.includes(environment.api_server, 'api-dev');
    // logo
    this.authService.getLogo('logo-horizontal').subscribe(base64 => {
      if (base64) this.logo = base64;
    });
    // get username from token
    this.username = this.authService.getTokenFieldValue('name');
    // get available tenants for user
    this.tenants = _.orderBy(this.authService.getTokenFieldValue('tenants'), 'name');
    // build user menu items
    this.itemsUser = [
      {
        label: this.translate.instant('button.change-password'),
        icon: 'pi pi-key',
        command: () => {
          this.changePassword();
        }
      },
      {
        label: this.translate.instant('button.logout'),
        icon: 'pi pi-power-off',
        command: () => {
          this.authService.logout();
        }
      }
    ];
    // load tenants form
    this.loadForm();
    // handle update last timesheet in header from other views
    this.timesheetService.lastTimesheetObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if(_.includes(this.roles, 'employee')) {
          this.lastTimesheetRecord = await this.getLastTimesheetRecord();
          this.buildLastTimesheetRecordItem();
        }
      });
  }

  private async getLastTimesheetRecord(): Promise<any> {
    try {
      const res: any = await firstValueFrom(
        this.timesheetService.getLastTimesheetRecord().pipe(
          takeUntil(this.destroy$),
          defaultIfEmpty(undefined),
        )
      );
      this.logger.debug('[timesheetService] getLastTimesheetRecord res: ', res);
      return _.get(res, 'body.data');
    } catch (err) {
      this.logger.error('[timesheetService] getLastTimesheetRecord err:', err);
      this.auxiliarService.toast(this.translate.instant('error.timesheet-last'), 'error');
      return undefined;
    }
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      tenant: ['', []],
    });

    this.f['tenant'].valueChanges
      .pipe(distinctUntilChanged((prev: Tenant, curr: Tenant) => prev?.id === curr?.id))
      .subscribe(async (value: Tenant) => {
        this.roles = _.split(_.get(value, 'roles', ''), ',');
        const tenantChange = value.id !== this.cookieService.get('tenant');
        this.buildMenuBarItems(tenantChange);
        this.tenantService.setAppTenant(value);
        this.cookieService.set('tenant', value.id, { 'path': '/' });
        // get last timesheet (if employee role)
        if(_.includes(this.roles, 'employee')) {
          this.lastTimesheetRecord = await this.getLastTimesheetRecord();
          this.buildLastTimesheetRecordItem();
        }
      });

    const initialTenant = this.cookieService.get('tenant') || this.authService.getTokenFieldValue('defaultTenant');
    this.f['tenant'].setValue(_.find(this.tenants, {id: initialTenant}) || _.first(this.tenants));
  }

  private buildMenuBarItems(tenantChange: boolean): void {
    const urlTenant = this.cookieService.get('urlTenant') || 'benefit';
    const admin = this.authService.getTokenFieldValue('isAdmin');
    this.items = [
      {
        label: this.translate.instant('menu.home'),
        icon: 'pi pi-home',
        route: `/${urlTenant}/dashboard`,
        visible: admin || _.some(['auxiliar', 'frontdesk', 'manager', 'professional'], (role) => _.includes(this.roles, role)),
        isActive: () => _.includes(this.router.url, '/dashboard'),
      },
      {
        label: this.translate.instant('menu.patients'),
        icon: 'pi pi-heart',
        route: `/${urlTenant}/patients`,
        visible: admin || _.some(['auxiliar', 'frontdesk', 'manager', 'professional'], (role) => _.includes(this.roles, role)),
        isActive: () => _.includes(this.router.url, '/patients') || _.includes(this.router.url, '/medical-records'),
      },
      {
        label: this.translate.instant('menu.schedule'),
        icon: 'pi pi-calendar-clock',
        route: `/${urlTenant}/schedule`,
        visible: admin || _.some(['auxiliar', 'frontdesk', 'manager', 'professional'], (role) => _.includes(this.roles, role)),
        isActive: () => _.includes(this.router.url, '/schedule'),
      },
      {
        label: this.translate.instant('menu.clients'),
        icon: 'pi pi-shopping-bag',
        route: `/${urlTenant}/products`,
        visible: admin || _.includes(this.roles, 'manager'),
        isActive: () => _.includes(this.router.url, '/products'),
      },
      {
        label: this.translate.instant('menu.quotes'),
        icon: 'pi pi-euro',
        route: `/${urlTenant}/quotes`,
        visible: admin || _.some(['frontdesk', 'manager'], (role) => _.includes(this.roles, role)),
        isActive: () => _.includes(this.router.url, '/quotes'),
      },
      {
        label: this.translate.instant('menu.billing'),
        icon: 'pi pi-paperclip',
        route: `/${urlTenant}/billing`,
        visible: admin || _.includes(this.roles, 'billing'),
        isActive: () => _.includes(this.router.url, '/billing'),
      },
      {
        label: this.translate.instant('menu.timesheets'),
        icon: 'pi pi-clock',
        route: `/${urlTenant}/timesheets`,
        visible: admin || _.includes(this.roles, 'employee'),
        isActive: () => _.includes(this.router.url, '/timesheets'),
      },
      {
        label: this.translate.instant('menu.employees'),
        icon: 'pi pi-users',
        route: `/${urlTenant}/employees`,
        visible: admin || _.includes(this.roles, 'manager'),
        isActive: () => _.includes(this.router.url, '/employees'),
      },
      {
        label: this.translate.instant('menu.tenants'),
        icon: 'pi pi-building',
        route: `/${urlTenant}/tenants`,
        visible: admin,
        isActive: () => _.includes(this.router.url, '/tenants'),
      },
    ];

    // set menu breakpoint (collapse limit)
    this.refreshMenu();

    // control menu changes to save active item
    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.activeItem = event.urlAfterRedirects;
      });

    // redirect according to default tenant roles if necessary
    if(tenantChange && 
      ((this.activeItem && !_.get(_.find(this.items, {route: this.activeItem}), 'visible')) || _.includes(this.router.url, '/medical-records'))) {
      this.authService.redirectAccordingToRoles(this.roles);
    }
  }

  private refreshMenu(): void {
    // fade out
    this.showMenuFade = false;
    setTimeout(() => {
      // destroy menu bar
      this.showMenu = false;
      // recalc breakpoint
      setTimeout(() => {
        this.menuBreakpoint = `${_.filter(this.items, 'visible').length * 210}px`;
        this.showMenu = true;
        // fade in
        setTimeout(() => {
          this.showMenuFade = true;
        }, 50);
      });
    }, 300);
  }

  private buildLastTimesheetRecordItem(): void {
    // if item exists, take it, if not, create new object
    let item = _.find(this.itemsUser, {id: 'last-timesheet'}) || {
        id: 'last-timesheet',
        label: this.translate.instant('last-timesheet'),
        icon: 'pi pi-clock',
        command: () => {},
    };
    _.set(item, 'action', this.lastTimesheetRecord?.action);
    switch(this.lastTimesheetRecord.action) {
      case 'break_begin':
      case 'work_begin':
        _.set(item, 'record', this.lastTimesheetRecord?.record ?
          moment(this.lastTimesheetRecord.record).format('HH:mm') : '');
        break;
      default:
        _.set(item, 'timesheet', true);
        _.set(item, 'label', this.translate.instant('button.init-work'));
    }
    // if item doesn't exist, unshift new item to user items array
    if(!_.some(this.itemsUser, {id: 'last-timesheet'})) this.itemsUser?.unshift(item);
  }

  public isCollapsed(): boolean {
    return this.menubarButton?.nativeElement.offsetParent !== null;
  }

  // open change password drawer
  private changePassword(): void {
    this.auxiliarService.openDrawer(ChangePasswordDrawerComponent, {
      position: 'right',
      iconRight: 'pi pi-key',
      titleRight: this.translate.instant('title.password-change'),
      width: '600px'
    });
  }

  // record new timesheet record
  public createTimesheetRecord(action: string): void {
    const timesheet = { action };
    this.timesheetService.createTimesheetRecord(timesheet).pipe(
      finalize(() => this.loadingTimesheet = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (res: any) => {
        this.logger.debug('[timesheetService] createTimesheetRecord res: ', res);
        // get last timesheet
        this.lastTimesheetRecord = await this.getLastTimesheetRecord();
        // rebuild last record menu item
        this.buildLastTimesheetRecordItem();
        // refresh user timesheets
        this.timesheetService.updateUserTimesheets(this.lastTimesheetRecord);
      },
      error: (err: any) => {
        this.logger.error('[timesheetService] createTimesheetRecord err:', err);
        this.auxiliarService.toast(this.translate.instant('error.timesheet-create'), 'error');
      }
    });
  }
}
